/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * Standardized processor type constants
 * These are the ONLY supported processor types in the application
 *
 * @readonly
 */
export const PROCESSOR_TYPES = {
  /** Document classification processor */
  CLASSIFICATION: "CUSTOM_CLASSIFICATION_PROCESSOR",
  /** Document splitting processor */
  SPLITTING: "CUSTOM_SPLITTING_PROCESSOR",
  /** Data extraction processor */
  EXTRACTION: "CUSTOM_EXTRACTION_PROCESSOR"
} as const;

/**
 * UI internal tab types used by the DocumentInput component
 */
export type TabType = "DocSplitter" | "DAI_COMPLEX_CLASSIFICATION" | "Entity";

/**
 * Review request types used internally
 */
export type ReviewRequestType =
  | "DocSplitter"
  | "DAI_COMPLEX_CLASSIFICATION"
  | "Entity"
  | "Firestore";

/**
 * Maps processor types to UI internal tab types
 */
export const PROCESSOR_TO_TAB_TYPE_MAP: Record<string, TabType> = {
  [PROCESSOR_TYPES.CLASSIFICATION]: "DAI_COMPLEX_CLASSIFICATION",
  [PROCESSOR_TYPES.SPLITTING]: "DocSplitter",
  [PROCESSOR_TYPES.EXTRACTION]: "Entity"
};

/**
 * Maps processor types to review request types
 */
export const PROCESSOR_TO_REVIEW_TYPE_MAP: Record<string, ReviewRequestType> = {
  [PROCESSOR_TYPES.CLASSIFICATION]: "DAI_COMPLEX_CLASSIFICATION",
  [PROCESSOR_TYPES.SPLITTING]: "DocSplitter",
  [PROCESSOR_TYPES.EXTRACTION]: "Entity"
};

/**
 * Maps processor types to their display names
 */
export const PROCESSOR_DISPLAY_NAMES: Record<string, string> = {
  [PROCESSOR_TYPES.CLASSIFICATION]: "Document Classification",
  [PROCESSOR_TYPES.SPLITTING]: "Document Splitting",
  [PROCESSOR_TYPES.EXTRACTION]: "Data Extraction"
};

/**
 * Utility function to get tab type from processor type
 */
export function getTabTypeFromProcessorType(processorType: string): TabType {
  const tabType = PROCESSOR_TO_TAB_TYPE_MAP[processorType];
  if (!tabType) {
    console.warn(`Unknown processor type: ${processorType}. Defaulting to Entity extraction.`);
    return "Entity";
  }
  return tabType;
}

/**
 * Utility function to get review request type from processor type
 */
export function getReviewTypeFromProcessorType(processorType: string): ReviewRequestType {
  const reviewType = PROCESSOR_TO_REVIEW_TYPE_MAP[processorType];
  if (!reviewType) {
    console.warn(`Unknown processor type: ${processorType}. Defaulting to Entity extraction.`);
    return "Entity";
  }
  return reviewType;
}

/**
 * Utility function to check if a processor type is valid
 */
export function isValidProcessorType(processorType: string): boolean {
  return processorType in PROCESSOR_TO_TAB_TYPE_MAP;
}

/**
 * Utility function to get display name for processor type
 */
export function getProcessorDisplayName(processorType: string): string {
  return PROCESSOR_DISPLAY_NAMES[processorType] || "Unknown Processor";
}
